import React, { useEffect, useRef, useState } from 'react'
import { FlexDiv } from './flexdiv'
import InputBox from './InputBox'

const CONTENT_BORDER = 3
const BOX_PADDING = 10
const NAV_SECTION_WIDTH = 30
const COLUMN_GAP = 200

export interface IBottomTreeColumnNode {
  id: string
  text: string
  value: string
  calc?: (columns: IBottomTreeColumn[], fromLeft: boolean, primaryId: string, extraData?: any) => string | undefined
}

export interface IBottomTreeColumn {
  top?: IBottomTreeColumnNode
  bottom: IBottomTreeColumnNode
}

interface IBottomTree {
  columns: IBottomTreeColumn[]
  onValueChanged: (id: string, value: string) => void
}

export default function BottomTree({ columns, onValueChanged }: IBottomTree) {
  const containerRef = useRef<HTMLDivElement>(null)
  const baseBottomRef = useRef<HTMLDivElement>(null)
  const rightTopRefs = useRef<(HTMLDivElement | null)[]>([])
  const rightBottomRefs = useRef<(HTMLDivElement | null)[]>([])

  //   const columns = [
  //     { top: 'Base top 1', bottom: 'Base bund 2' },
  //     { top: 'Højre 1 top\nasdasdasd\nsadasdasdasd\nasdasdasd\nsadasdasdasd\nasdasdasd\nsadasdasdasd', bottom: 'Højre 1 bund' },
  //     { top: 'Højre 2 top', bottom: 'Højre 2 bund' },
  //     { top: 'Højre 3 top', bottom: 'Højre 3 bund' },
  //     { top: 'Højre 4 top\nasdasdasd\nsadasdasdasd\nasdasdasd\nsadasdasdasd\nasdasdasd\nsadasdasdasd', bottom: 'Højre 4 bund' },
  //   ]

  const [globalOffset, setGlobalOffset] = useState(0)
  const [offsets, setOffsets] = useState<(number | undefined)[]>(() => columns.map(() => undefined))
  const [topHeights, setTopHeights] = useState<(number | undefined)[]>(() => columns.map(() => undefined))
  const [bottomHeights, setBottomHeights] = useState<(number | undefined)[]>(() => columns.map(() => undefined))
  const [allOk, setAllOk] = useState(false)

  const [measureNonce, setMeasureNonce] = useState(0)

  const getHeight = (el: HTMLDivElement) => {
    const r = el.getBoundingClientRect()
    return r.height
  }

  const getTopY = (el: HTMLDivElement, container: HTMLDivElement) => {
    const c = container.getBoundingClientRect()
    const r = el.getBoundingClientRect()
    return r.top - c.top
  }

  const getCenterY = (el: HTMLDivElement, container: HTMLDivElement) => {
    const c = container.getBoundingClientRect()
    const r = el.getBoundingClientRect()
    return r.top - c.top + r.height / 2
  }

  const arraysClose = (a: number[], b: number[], tol = 0.5) => {
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) {
      if (Math.abs(a[i] - b[i]) > tol) return false
    }
    return true
  }

  useEffect(() => {
    const container = containerRef.current
    const base = baseBottomRef.current
    if (!container || !base) return

    let frame = 0
    let prev: number[] | null = null
    let iterations = 0
    const maxIterations = 10
    let highestTop = 0

    const run = () => {
      const next: number[] = []
      const heightTops: number[] = []
      const heightBottoms: number[] = []
      let anchor: HTMLDivElement = base

      for (let i = 0; i < columns.length - 1; i++) {
        const center = getCenterY(anchor, container)
        next.push(center)

        const thisTop = rightTopRefs.current[i]
        if (thisTop) {
          const top = getTopY(thisTop, container)
          highestTop = Math.min(highestTop, top)
          const heightTop = getHeight(thisTop)
          heightTops.push(heightTop)
        } else {
          heightTops.push(0)
        }

        const thisBottom = rightBottomRefs.current[i]
        if (thisBottom) {
          anchor = thisBottom
          const heightBottom = getHeight(thisBottom)
          heightBottoms.push(heightBottom)
        } else {
          heightBottoms.push(0)
        }
      }

      //console.log(`highestTop = ${highestTop}`)

      setTopHeights(heightTops)
      setBottomHeights(heightBottoms)

      setGlobalOffset(highestTop)

      let allIsOk = false
      setOffsets(prev => {
        if (prev.length !== next.length) return next

        for (let i = 0; i < prev.length; i++) {
          if (prev[i] !== next[i]) return next
        }

        allIsOk = true
        return prev
      })

      setAllOk(allIsOk)

      // hvis vi ikke har en tidligere måling, så mål igen
      if (!prev) {
        prev = next
        iterations++
        frame = requestAnimationFrame(run)
        return
      }

      // hvis det ikke har stabiliseret sig endnu, mål igen
      if (!arraysClose(prev, next) && iterations < maxIterations) {
        prev = next
        iterations++
        frame = requestAnimationFrame(run)
        return
      }

      // ellers stopper vi bare
    }

    frame = requestAnimationFrame(run)

    return () => cancelAnimationFrame(frame)
  }, [columns.length, measureNonce, offsets])

  // lyt på resize så vi kan måle igen
  // useEffect(() => {
  //   const onResize = () => {
  //     setMeasureNonce(x => x + 1)
  //   }
  //   window.addEventListener('resize', onResize)
  //   return () => window.removeEventListener('resize', onResize)
  // }, [])

  //console.log(offsets)

  const bbb = columns.length > 0 && (
    <div style={{ display: 'flex', flexDirection: 'column' /*, gap: 12*/ }}>
      {!!columns[0].top?.id && (
        <div style={boxTop}>
          <FlexDiv style={boxInner}>
            <InputBox
              title={columns[0].top.text}
              maxWidth={COLUMN_GAP}
              value={columns[0].top.value}
              onChange={value => onValueChanged(columns[0].top!.id, value)}
              suffix="O"
            />
          </FlexDiv>
        </div>
      )}
      <div ref={baseBottomRef} style={boxBottom}>
        <FlexDiv style={boxInner}>
          <InputBox
            title={columns[0].bottom.text}
            maxWidth={COLUMN_GAP}
            value={columns[0].bottom.value}
            onChange={value => onValueChanged(columns[0].bottom!.id, value)}
            suffix="W"
          />
        </FlexDiv>
      </div>
    </div>
  )

  const maxLeft = Math.max(
    ...columns.slice(1).map((col, i) => (i + 1) * (COLUMN_GAP + NAV_SECTION_WIDTH * 2 + BOX_PADDING * 2 + CONTENT_BORDER) - NAV_SECTION_WIDTH * 2),
  )
  const maxTop = Math.max(...columns.slice(1).map((col, i) => offsets[i] ?? 0))

  const xxx = columns.slice(1).map((col, i) => (
    <FlexDiv
      key={i}
      style={{
        position: 'absolute',
        left: (i + 1) * (COLUMN_GAP + NAV_SECTION_WIDTH * 2 + BOX_PADDING * 2 + CONTENT_BORDER) - NAV_SECTION_WIDTH * 2,
        top: offsets[i],
        transform: 'translateY(-50%)',
        display: 'flex',
        //flexDirection: 'column',
        //gap: 12,
        transition: allOk ? 'top 0.2s ease-out' : undefined,
        alignItems: 'stretch',
        justifyContent: 'stretch',
        //border: '3px solid blue'
      }}
    >
      {/* boxNavContainer virker fordi den altid er centeret omkring forrige bottom */}
      <FlexDiv style={boxNavContainer}>
        <FlexDiv direction="column" style={boxNavContainerLeft}>
          <div style={boxNavNone}></div>
          <div style={boxNavTop}></div>
        </FlexDiv>
        <FlexDiv direction="column" style={boxNavContainerRight}>
          <FlexDiv direction="column" flex={1} style={boxNavContainerRightContainer}>
            <FlexDiv style={boxNavContainerRightTopTop((topHeights[i] ?? 0) / 2)} />
            <FlexDiv />
            <FlexDiv style={boxNavContainerRightTopBottom} />
            <FlexDiv />
          </FlexDiv>
          <FlexDiv direction="column" flex={1} style={boxNavContainerRightContainer}>
            <FlexDiv style={boxNavContainerRightBottomTop} />
            <FlexDiv />
            <FlexDiv style={boxNavContainerRightBottomBottom((bottomHeights[i] ?? 0) / 2)} />
            <FlexDiv />
          </FlexDiv>
        </FlexDiv>
      </FlexDiv>

      <FlexDiv direction="column">
        <FlexDiv direction="row" ref={el => (rightTopRefs.current[i] = el)} style={boxTop}>
          <FlexDiv style={boxInner}>
            {!!col.top && <InputBox title={col.top.text} maxWidth={COLUMN_GAP} value={col.top.value} onChange={value => onValueChanged(col.top!.id, value)} />}
          </FlexDiv>
        </FlexDiv>
        <FlexDiv direction="row" ref={el => (rightBottomRefs.current[i] = el)} style={boxBottom}>
          <FlexDiv style={boxInner} direction="column" gap={10}>
            <InputBox title={col.bottom.text} maxWidth={COLUMN_GAP} value={col.bottom.value} onChange={value => onValueChanged(col.bottom!.id, value)} />
          </FlexDiv>
        </FlexDiv>
      </FlexDiv>
    </FlexDiv>
  ))

  return (
    <div style={{ display: 'inline-block', paddingRight: maxLeft + NAV_SECTION_WIDTH * 2, paddingBottom: maxTop - globalOffset }}>
      <div
        ref={containerRef}
        style={{
          position: 'relative',
          display: 'flex',
          top: -globalOffset,
          //border: '2px solid red',
          // width: 'auto'
        }}
      >
        {bbb}
        {xxx}
      </div>
    </div>
  )
}

const boxNavContainerRightContainer: React.CSSProperties = {
  flex: 1,
}

const boxNavContainerRightTopTop = (height: number | undefined) =>
  ({
    flex: height ? undefined : 1,
    borderBottomWidth: CONTENT_BORDER,

    borderBottomColor: 'black',
    borderBottomStyle: 'solid',
    // backgroundColor: 'red',
    height,
  } as React.CSSProperties)

const boxNavContainerRightTopBottom = {
  flex: 1,
  borderLeftWidth: CONTENT_BORDER,
  borderLeftColor: 'black',
  borderLeftStyle: 'solid',
  //backgroundColor: 'green',
} as React.CSSProperties

const boxNavContainerRightBottomTop = {
  flex: 1,
  borderLeftWidth: CONTENT_BORDER,
  borderLeftColor: 'black',
  borderLeftStyle: 'solid',
  //backgroundColor: 'green',
} as React.CSSProperties

const boxNavContainerRightBottomBottom = (height: number | undefined) =>
  ({
    flex: height ? undefined : 1,
    borderTopWidth: CONTENT_BORDER,

    borderTopColor: 'black',
    borderTopStyle: 'solid',
    // backgroundColor: 'red',
    height,
  } as React.CSSProperties)

const boxNavContainer: React.CSSProperties = {
  alignItems: 'stretch',
  alignSelf: 'stretch',
  //   backgroundColor: 'brown',
}

const boxNavContainerLeft: React.CSSProperties = {
  flexDirection: 'column',
  alignItems: 'stretch',
  width: NAV_SECTION_WIDTH,
}

const boxNavContainerRight: React.CSSProperties = {
  flexDirection: 'column',
  alignItems: 'stretch',
  width: NAV_SECTION_WIDTH,
}

const boxNavNone: React.CSSProperties = {
  flex: 1,
  //backgroundColor: 'yellow',
}

const boxNavTop: React.CSSProperties = {
  borderTopStyle: 'solid',
  borderTopWidth: CONTENT_BORDER,
  borderTopColor: 'black',
  //backgroundColor: 'blue',
  flex: 1,
}

const boxTop: React.CSSProperties = {
  background: '#ddd',
  padding: BOX_PADDING,
  borderRadius: 6,
  minWidth: COLUMN_GAP,
  maxWidth: COLUMN_GAP,
  //minHeight: 50,
  borderColor: 'black',
  borderWidth: CONTENT_BORDER,
  borderStyle: 'solid',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 30,
}

const boxBottom: React.CSSProperties = {
  background: '#ddd',
  padding: BOX_PADDING,
  borderRadius: 6,
  minWidth: COLUMN_GAP,
  maxWidth: COLUMN_GAP,
  //minHeight: 50,
  borderColor: 'black',
  borderWidth: CONTENT_BORDER,
  borderStyle: 'solid',
  alignItems: 'center',
  justifyContent: 'center',
}

const boxInner: React.CSSProperties = {
  borderColor: 'black',
  borderWidth: 0,
  borderStyle: 'solid',
  //padding: 20,
  flex: 1,
  whiteSpace: 'pre-line',
  //textAlign: 'center',
  justifyContent: 'center',
}
